<template>
  <Dialog :open="true" @close="props.onClose">
    <div class="fixed inset-0 flex items-center justify-center p-4 z-20 bg-zinc-700/90 no-select">
      <DialogPanel class="border border-white w-[800px] max-h-[90vh] flex flex-col bg-black">
        <DialogTitle class="border-b border-white p-3 text-yellow-300">
          {{ text('ui.settings_title') }}
        </DialogTitle>
        <!-- language section -->
        <div class="p-4 w-full flex justify-center">
          <!-- English -->
          <div class="mr-16 flex">
            <input
              id="en"
              v-model="state.selected_language"
              class="cursor-pointer"
              type="radio"
              value="en"
              @input="onSelectedLanguageChange"
            />
            <span class="mx-2">{{ Locales.en.emoji }}</span>
            <span>{{ Locales.en.name }}</span>
          </div>
          <!-- Russian -->
          <div class="flex">
            <input
              id="ri"
              v-model="state.selected_language"
              class="cursor-pointer"
              type="radio"
              value="ru"
              @input="onSelectedLanguageChange"
            />
            <span class="mx-2">{{ Locales.ru.emoji }}</span>
            <span>{{ Locales.ru.name }}</span>
          </div>
        </div>
        <!-- profanity filter -->
        <div class="w-full pb-4 flex justify-center">
          <input
            id="profanity_filter"
            v-model="state.filter_profanity"
            class="cursor-pointer"
            type="checkbox"
            @input="SettingsActions.toggleProfanityFilter"
          />
          <label for="profanity_filter" class="ml-2 text-neutral-400">
            {{ text('ui.profanity_filter') }}
          </label>
        </div>
        <!-- sound section -->
        <div class="w-100 p-4 flex flex-col items-center">
          <!-- music -->
          <div class="w-[60%]">
            <div class="text-sm text-neutral-400">{{ text('ui.music') }}</div>
            <!-- <Slider defaultValue={this.state.musicVolume} onChange={this.onMusicChange}/> -->
            <VueSimpleRangeSlider
              v-model="state.music_volume"
              class="-mt-8 w-100"
              :min="0"
              :max="100"
              @update:model-value="onMusicChange"
            />
          </div>
          <!-- sfx -->
          <div class="mt-2 w-[60%]">
            <div class="text-sm text-neutral-400">
              {{ text('ui.sound_effects') }}
            </div>
            <!-- <Slider defaultValue={this.state.musicVolume} onChange={this.onMusicChange}/> -->
            <VueSimpleRangeSlider
              v-model="state.sfx_volume"
              class="-mt-8 w-100"
              :min="0"
              :max="100"
              @update:model-value="onSFXChange"
            />
          </div>
        </div>
        <hr />
        <!-- credits section -->
        <div class="p-4 text-center">
          <!-- credits title -->
          <div class="mb-6 text-yellow-300">{{ text('ui.credits') }}</div>
          <!-- credits container -->
          <div class="flex flex-wrap justify-center gap-x-24 gap-y-6">
            <!-- lead: Rob -->
            <div class="flex flex-col items-center">
              <div class="font-bold">{{ text('ui.lead_developer') }}</div>
              <div class="text-blue-500">Robert Lombardo</div>
              <a
                href="https://wakefield-studios.com"
                target="_blank"
                :class="{
                  '-mt-2 w-[170px] cursor-pointer': true,
                  ['pointer-events-none']: ['game_distribution', 'yandex'].includes(
                    Config.PLATFORM
                  ),
                }"
              >
                <img :src="`${Config.DOM_IMG_PATH}/wfs_logo_white.png`" />
              </a>
            </div>
            <!-- visuals: Otto -->
            <div class="flex flex-col items-center">
              <div class="font-bold">{{ text('ui.graphic_designer') }}</div>
              <div class="text-blue-500">Otto Chin</div>
            </div>
            <!-- composer: Mattias -->
            <div class="flex flex-col items-center">
              <div class="font-bold">{{ text('ui.composer') }}</div>
              <div class="text-blue-500">Mattias Holmgren</div>
              <a
                href="https://www.morningdewmedia.com/"
                target="_blank"
                :class="{
                  'mt-2 w-[100px] cursor-pointer': true,
                  ['pointer-events-none']: ['game_distribution', 'yandex'].includes(
                    Config.PLATFORM
                  ),
                }"
              >
                <img :src="`${Config.DOM_IMG_PATH}/morningdew_logo.png`" />
              </a>
            </div>
            <!-- Bizdev: Dan -->
            <!-- <div class="flex flex-col items-center">
              <div class="font-bold">{{ text('ui.business_development') }}</div>
              <div class="text-blue-500">Daniel Knott</div>
            </div> -->
            <!-- QA Lead: Kele -->
            <!-- <div class="flex flex-col items-center">
              <div class="font-bold">{{ text('ui.qa_lead') }}</div>
              <div class="text-blue-500">Dániel Kelemen</div>
            </div> -->
            <!-- Project Manager: Dylan -->
            <!-- <div class="flex flex-col items-center">
              <div class="font-bold">{{ text('ui.project_manager') }}</div>
              <div class="text-blue-500">Dylan Quast</div>
            </div> -->
          </div>
        </div>
        <hr />
        <div class="p-4 text-center">
          <!-- Everywhere but Yandex & GameDistribution, links to Terms of Service & Privacy Policy -->
          <div v-if="!['game_distribution', 'yandex'].includes(Config.PLATFORM)">
            <a
              class="px-3 text-neutral-400 underline text-xs"
              href="https://wakefield-studios.com/#/terms-of-service"
              target="_blank"
            >
              {{ text('ui.terms_of_service') }}
            </a>
            <a
              class="px-3 text-neutral-400 underline text-xs"
              href="https://wakefield-studios.com/#/privacy-policy"
              target="_blank"
            >
              {{ text('ui.privacy_policy') }}
            </a>
          </div>
          <div class="px-32 text-xs text-neutral-300">
            {{ text('ui.customer_support_email') }}
          </div>
          <div v-if="state.newsletter_email" class="mt-2 flex flex-col items-center">
            <button
              class="px-3 py-1 text-xs underline text-neutral-200 disabled:opacity-50"
              :disabled="state.pending_newsletter_unsubscribe"
              @click="onNewsletterUnsubscribeClick"
            >
              {{ text('ui.unsubscribe') }}
            </button>
            <div v-if="state.newsletter_error" class="mt-2 text-xs text-red-500">
              {{ state.newsletter_error }}
            </div>
          </div>
          <div v-if="state.newsletter_unsubscribe_success" class="mt-2 text-xs text-green-400">
            {{ text('ui.newsletter_unsubscribe_success') }}
          </div>
        </div>
        <!-- CrazyGames requires no Log Out option -->
        <div v-if="Config.PLATFORM !== 'crazygames'">
          <hr />
          <div class="p-4 text-center">
            <a class="text-red-800 cursor-pointer" @click="onLogOutClick">
              {{ text('ui.log_out') }}
            </a>
          </div>
        </div>
        <!-- Version display -->
        <div class="p-4 pt-2 w-full text-right text-xs text-neutral-500">v{{ appVersion }}</div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import firebase from 'firebase/compat/app';
import { onBeforeMount, onBeforeUnmount, reactive } from 'vue';
import VueSimpleRangeSlider from 'vue-simple-range-slider';
import 'vue-simple-range-slider/css';
import Audio from '~/Audio';
import { Config, Locales } from '~/constants';
import { PlayerActions, SettingsActions } from '~/flux/actions';
import { PlayerStore, SettingsStore } from '~/flux/stores';
import text from '~/text';
import Tools from '~/Tools';

const props = defineProps({
  onClose: {
    type: Function,
    required: true,
  },
});

const state = reactive({
  selected_language: Config.LOCALE,
  filter_profanity: SettingsStore.getAll().filter_profanity,
  music_volume: SettingsStore.getAll().musicVolume * 100,
  sfx_volume: SettingsStore.getAll().sfxVolume * 100,
  newsletter_email:
    PlayerStore.getAll()?.social?.newsletter_email,
  pending_newsletter_unsubscribe: false,
  newsletter_error: '',
  newsletter_unsubscribe_success: false,
});

onBeforeMount(() => {
  PlayerStore.on(PlayerStore.NEWSLETTER_UNSUBSCRIBE_SUCCESS, onNewsletterUnsubscribeSuccess);
  PlayerStore.on(PlayerStore.NEWSLETTER_UNSUBSCRIBE_ERROR, onNewsletterUnsubscribeError);
});

onBeforeUnmount(() => {
  PlayerStore.removeListener(
    PlayerStore.NEWSLETTER_UNSUBSCRIBE_SUCCESS,
    onNewsletterUnsubscribeSuccess
  );
  PlayerStore.removeListener(PlayerStore.NEWSLETTER_UNSUBSCRIBE_ERROR, onNewsletterUnsubscribeError);
});

function onSelectedLanguageChange(event) {
  SettingsActions.changeBrowserLanguage(event.target.value);
}

function onMusicChange(val) {
  Audio.setMusicVolume(val / 100);
  saveSettings();
}

function onSFXChange(val) {
  Audio.setSFXVolume(val / 100);
  saveSettings();
}

const saveSettings = Tools.debounce(() => {
  SettingsActions.saveSettings({
    musicVolume: Audio.getMusicVolume(),
    sfxVolume: Audio.getSFXVolume(),
  });
}, 100);

function onNewsletterUnsubscribeClick(event) {
  event.preventDefault();
  state.newsletter_error = '';
  state.newsletter_unsubscribe_success = false;

  state.pending_newsletter_unsubscribe = true;
  const email = state.newsletter_email?.trim();
  PlayerActions.submitNewsletterUnsubscribe(email || undefined);
}

function onNewsletterUnsubscribeSuccess() {
  state.newsletter_email = null;
  state.pending_newsletter_unsubscribe = false;
  state.newsletter_unsubscribe_success = true;
}

function onNewsletterUnsubscribeError(detail) {
  state.pending_newsletter_unsubscribe = false;
  state.newsletter_error = detail || 'An error occurred';
}

function onLogOutClick() {
  firebase.auth().signOut();
  window.location.reload();
}

const appVersion = __APP_VERSION__;
</script>

<style>
.simple-range-slider-popover {
  display: none;
}

.simple-range-slider-popover-arrow {
  display: none;
}
</style>
